<template>

<!--
    Request Autocomplete Component
    Lists WorkOrders where Title='Request' (actual customer requests)
    
    @version 1.0
-->

<div class="w-100">
    <!-- Label slot for customization -->
    <div class="d-flex align-items-center justify-content-between mb-1">
        <span class="form-label mb-0">
            <slot name="label">{{ label }}</slot>
        </span>
        <slot name="label-actions"></slot>
    </div>

    <div class="input-group w-100 flex-nowrap border-0">

    <!-- request auto complete -->
    <kt-autocomplete
        class="flex-grow-1"
        inputclass="form-control rounded-end-0 form-control-sm"
        :options="filteredList"
        rowvalue="RequestTitle"
        :rowkey="props.rowkey"
        :placeholder="props.placeholder"
        :display-by="customRequestLabel"
        @change="onChange"
        v-model="selectedId"
        suffix-class="p-0"
        :min-width="350"
    >

    <template #suffix>

        <!-- filter requests -->
        <kt-multi-select
            css="rounded-0"
            placeholder="<i class='fa fa-filter'></i>"
            :options="statusOptions"
            labelKey="label"
            valueKey="value"
            v-model="statusFilter"
        >
        </kt-multi-select>

        <!-- edit request button -->
        <button 
            v-if="selectedId"
            @click.stop.prevent="editRequest(selectedId)" 
            class="btn btn-sm btn-outline-secondary rounded-start-0 rounded-end-0"
            type="button"
        ><i class="fa fa-edit"></i>
        </button>

        <!-- new request button -->
        <button 
            @click.stop.prevent="editRequest()"
            class="btn btn-sm btn-outline-primary rounded-start-0"
            type="button"
        ><i class="fa fa-plus"></i>
        </button>
    </template>

    </kt-autocomplete>

</div>
</div>

<kt-modal
    :title="formTitle"
    :visible="formVisible"
    :size="'modal-xl'"
    @dismiss="formDismiss()"
>
    <template #body>
        <work-order-form
            v-model="form"
        ></work-order-form>
    </template>

    <template #footer>
        <div class="-container-fluid -px-0 kt-modal-actions">
            <button 
                @click="formDismiss()" 
                class="btn btn-outline-secondary -form-control"
            ><X :size="16" class="me-1" />Close</button>
            <button 
                @click="formSave(form)" 
                class="btn btn-outline-primary -form-control"
            ><Save :size="16" class="me-1" />Save</button>
        </div>
    </template>
</kt-modal>

</template>

<script setup lang="ts">

import { ref, watch, onUnmounted, onMounted, computed } from "vue";
import { storeToRefs } from "pinia";

import ApiService from "@/services/ApiService";
import OptionsService from "@/services/OptionsService";
import { usePermissions } from "@/composables/usePermissions";
import KtAutocomplete from "@/components/UI/KtAutocomplete.vue";
import KtMultiSelect from "@/components/UI/KtMultiSelect.vue";
import KtModal from "@/components/UI/KtModal.vue";
import WorkOrderForm from "@/components/WorkOrders/WorkOrderForm.vue";
import { X, Save } from 'lucide-vue-next';
import { WorkOrderModel, WorkOrderFilter } from "@/models/WorkOrderModel";

// Model emits the full WorkOrder object (not just ID)
const model = defineModel<WorkOrderModel | null>({
    type: Object,
    default: null,
});

const props = defineProps({ 
    "rowkey": {
        type: String,
        default: "Id"
    },

    "label": {
        type: String,
        default: "Select Request"
    },
    
    "client": {
        type: String,
        default: ""
    },
    
    "inputClass": {
        type: String,
        default: ""
    },
    
    "placeholder": {
        type: String,
        default: "Choose client then select a request"
    },
    
    // Default status filter (array of status values)
    "status": {
        type: Array as () => number[],
        default: () => []
    },
});

const emits = defineEmits(["change", "status-change"]);
const { userId } = usePermissions();
const REQUESTAC_FILTER_KEY = 'requestac_status_filter';

// WorkOrder status options (different from Job status)
const statusOptions = ref([
    { label: 'Draft', value: 0 },
    { label: 'Pending', value: 1 },
    { label: 'Approved', value: 2 },
    { label: 'Complete', value: 3 },
    { label: 'Cancelled', value: 4 },
    { label: 'Finalized', value: 5 },
    { label: 'Assigned', value: -2 },
    { label: 'Deleted', value: -1 }
]);

const statusFilter = ref<number[]>([]);
let filterInitialized = false;
let statusWatchCount = 0;
let statusFilterWatchCount = 0;

// Normalize status prop to always be an array of numbers
const normalizeStatus = (status: number[] | undefined): number[] => {
    if (!status) return [];
    if (Array.isArray(status)) {
        return status.map(s => Number(s));
    }
    return [Number(status)];
};

// Sync statusFilter when status prop changes (only if not yet initialized from saved prefs)
watch(() => props.status, (newStatus) => {
    statusWatchCount++;
    if (statusWatchCount % 50 === 0) {
        console.warn(`⚠️ RequestAc props.status watcher fired ${statusWatchCount} times! INFINITE LOOP!`, newStatus);
    }
    console.debug(`🔄 RequestAc props.status watch (call #${statusWatchCount}):`, newStatus, 'filterInitialized:', filterInitialized);
    if (filterInitialized) return;
    const normalized = normalizeStatus(newStatus);
    if (normalized.length > 0) {
        statusFilter.value = normalized;
    }
}, { immediate: true });

const list = ref<WorkOrderModel[]>([]);
const selectedId = ref<string>("");
const form = ref<WorkOrderModel>(new WorkOrderModel());
const formTitle = ref("New Request");
const formVisible = ref(false);
const isLoadingRequests = ref(false);

const unwatch: (() => void)[] = [];

// Custom display label for dropdown items
const customRequestLabel = (row: WorkOrderModel): string => {
    // format should be: ID | Status - RequestTitle
    const statusOption = statusOptions.value.find(s => s.value === row.Status);
    const statusLabel = statusOption?.label || 'Unknown';
    const title = row.RequestTitle || row.Description || "No Title";
    const id = row.Id || '?';
    return `${id} | ${statusLabel} - ${title}`;
};

// Computed property to filter list based on status
const filteredList = computed(() => {
    if (!statusFilter.value || statusFilter.value.length === 0) {
        return list.value;
    }
    return list.value.filter(req => 
        statusFilter.value.includes(req.Status || -1)
    );
});

// Load WorkOrders with Title='Request' for the selected client
const loadRequests = async () => {
    if (!props.client) {
        list.value = [];
        selectedId.value = "";
        model.value = null;
        return;
    }

    isLoadingRequests.value = true;

    try {
        const filter: WorkOrderFilter = {
            Titles: ['Request'],
            ContactId: [Number(props.client)],
            Size: 9999999,
            Status: statusFilter.value.length > 0 ? statusFilter.value : undefined,
            IncludeRequest: true,  // Include parent Job info
            IncludeContact: true,  // Include client info
        };

        const response = await ApiService.loadWorkOrders(filter, false);
        
        if (response?.Data && Array.isArray(response.Data)) {
            list.value = response.Data;
        } else {
            list.value = [];
        }
    } catch (error) {
        console.error('Failed to load requests:', error);
        list.value = [];
    } finally {
        isLoadingRequests.value = false;
    }
};

// Handle selection change
const onChange = (value: string) => {
    const selectedRequest = list.value.find(r => String(r.Id) === value) || null;
    model.value = selectedRequest;
    emits("change", selectedRequest);
};

// Watch selectedId and update model when it changes
watch(selectedId, (newVal) => {
    if (!newVal) {
        model.value = null;
        return;
    }
    const selectedRequest = list.value.find(r => String(r.Id) === newVal) || null;
    model.value = selectedRequest;
});

// Watch model and sync selectedId
watch(model, (newVal) => {
    if (!newVal) {
        selectedId.value = "";
        return;
    }
    selectedId.value = String(newVal.Id);
}, { immediate: true });

// Edit or create a Request
const editRequest = async (val?: string) => {
    if (val) {
        // Edit existing
        const id = Number(val);
        formTitle.value = "Edit Request";
        try {
            form.value = await ApiService.loadWorkOrder(id);
        } catch (error) {
            console.error('Failed to load request:', error);
            return;
        }
    } else {
        // New request
        formTitle.value = "New Request";
        form.value = new WorkOrderModel();
        form.value.Title = "Request";  // Mark as Request type
        
        if (props.client) {
            form.value.ContactId = Number(props.client);
        }
    }

    formVisible.value = true;
};

// Watch for client changes and reload
unwatch.push(watch(() => props.client, async () => {
    await loadRequests();
}, { immediate: true }));

// Watch for status filter changes
unwatch.push(watch(() => statusFilter.value, async (nv, ov) => {
    statusFilterWatchCount++;
    if (statusFilterWatchCount % 50 === 0) {
        console.warn(`⚠️ RequestAc statusFilter watcher fired ${statusFilterWatchCount} times! INFINITE LOOP!`, nv);
    }
    console.debug(`🔄 RequestAc statusFilter watch (call #${statusFilterWatchCount}):`, nv, 'old:', ov);
    
    // Don't reload from API, just filter the existing list
    console.debug(`📡 RequestAc emitting status-change:`, nv);
    emits("status-change", nv);

    // Persist filter per user
    if (filterInitialized && userId.value) {
        try {
            const val = nv && nv.length > 0 ? JSON.stringify(nv) : null;
            await OptionsService.set(REQUESTAC_FILTER_KEY, val, 'Technician', String(userId.value));
        } catch (e) {
            console.warn('Failed to save RequestAc filter preference', e);
        }
    }
}));

const formDismiss = () => {
    formVisible.value = false;
};

const formSave = async (rec: WorkOrderModel) => {
    try {
        // Ensure Title is set to 'Request'
        rec.Title = "Request";
        
        const saved = await ApiService.saveWorkOrder(rec);
        await loadRequests();
        
        // If we were editing the currently selected request, update the model
        if (selectedId.value === String(saved.Id)) {
            model.value = saved;
            emits("change", saved);
        }
        
        formVisible.value = false;
    } catch (error) {
        console.error('Failed to save request:', error);
    }
};

onMounted(async () => {
    console.debug('🎬 RequestAc onMounted START');

    // Load saved filter preference for this user
    if (userId.value) {
        try {
            const saved = await OptionsService.get(REQUESTAC_FILTER_KEY, 'Technician', String(userId.value));
            if (saved) {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    statusFilter.value = parsed.map(Number);
                }
            }
        } catch (e) {
            console.warn('Failed to load RequestAc filter preference', e);
        }
    }
    filterInitialized = true;
    console.debug('✅ RequestAc filter initialized, saved filter applied:', statusFilter.value);
    console.debug('🎬 RequestAc onMounted END');
});

onUnmounted(() => {
    unwatch.forEach((val) => {
        val();
    });
});

</script>